import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Typography,
  Button,
  Snackbar,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import Swal from "sweetalert2";

const Principales = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingId, setLoadingId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Ajusta la cantidad de filas por página

  // Fetch inicial
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          "https://portaltransaccionalback.azurewebsites.net/api/GetPrincipales"
        );
        const data = await response.json();
        setData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Cambiar estado con confirmación
  const confirmToggleEstado = (id, currentEstado) => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: `¿Quieres ${
        currentEstado === 1 ? "desactivar" : "activar"
      } este formulario?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cambiar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        toggleEstado(id, currentEstado);
      }
    });
  };

  // Función para cambiar el estado
  const toggleEstado = async (id, currentEstado) => {
    setLoadingId(id);
    try {
      const newEstado = currentEstado == 1 ? 0 : 1;
      const response = await fetch(
        `https://portaltransaccionalback.azurewebsites.net/api/ActualizaEstadoForm/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response.ok) {
        setData((prevData) =>
          prevData.map((item) =>
            item.id == id ? { ...item, estado: newEstado } : item
          )
        );
      } else {
        const errorText = await response.text();
        console.error("Error al actualizar:", errorText);
      }
    } catch (error) {
      console.error("Error en la solicitud:", error);
    } finally {
      setLoadingId(null);
    }
  };

  // Copiar URL
  const handleCopyUrl = (publicacion) => {
    const url = `https://leadsicontec.azurewebsites.net/Publicado/${publicacion}`;
    navigator.clipboard
      .writeText(url)
      .then(() => setSnackbarOpen(true))
      .catch((err) => console.error("Error al copiar:", err));
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Navegación
  const handleSaveEdit = useCallback((id) => {
    window.location.href = `/Formulario/${id}`;
  }, []);

  const Nuevo = useCallback(() => {
    window.location.href = `/`;
  }, []);

  const Resultados = useCallback((id) => {
    window.location.href = `/Resultados/${id}`;
  }, []);
  const Clonar = useCallback((id) => {
    window.location.href = `/Duplica/${id}`;
  }, []);

  // Manejadores de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Volver a la primera página cuando se cambia la cantidad de filas
  };

  return (
    <Card sx={{ maxWidth: 1100, margin: "20px auto" }}>
      <CardContent>
        <Typography variant="h5" component="div" gutterBottom>
          Lista de Formularios
        </Typography>
        <Button variant="contained" color="primary" fullWidth onClick={Nuevo}>
          Crear Nuevo Formulario
        </Button>
        <hr />
        {loading ? (
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 20 }}
          >
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Titulo</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acceder</TableCell>
                  <TableCell>Resultados</TableCell>
                  <TableCell>Duplicar</TableCell>
                  <TableCell>Compartir URL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.titulo}</TableCell>
                      <TableCell>
                        {row.descripcion || "No disponible"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color={row.estado == 1 ? "success" : "warning"}
                          onClick={() =>
                            confirmToggleEstado(row.id, row.estado)
                          }
                          disabled={loadingId == row.id}
                        >
                          {loadingId == row.id ? (
                            <CircularProgress size={24} />
                          ) : row.estado == 1 ? (
                            "Activo"
                          ) : (
                            "Activar"
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={() => handleSaveEdit(row.id)}
                        >
                          Acceder
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="inherit"
                          fullWidth
                          onClick={() => Resultados(row.id)}
                        >
                          Resultados
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="info"
                          fullWidth
                          onClick={() => Clonar(row.id)}
                        >
                          Clonar
                        </Button>
                      </TableCell>
                      <TableCell>
                        {row.estado == 1 && (
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleCopyUrl(row.publicacion)}
                          >
                            Compartir
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message="URL copiada al portapapeles"
      />
    </Card>
  );
};

export default Principales;
